/**
 * QA Panel — Effects controller (POC animated lobby, ?fx=1).
 *
 * Fires the "over the top" effects on demand so they can be checked on a phone without waiting
 * for a real jackpot, switches the motion tier at runtime, and persists the boot flag
 * (localStorage, takes effect on reload) for the case where the lobby runs inside the game client
 * iframe and `?fx=1` cannot be added to the URL by hand.
 */

import {
  applyMotionTier,
  effectsBus,
  FX_ENABLED,
  getMotionTier,
  persistFxEnabled,
  resetFirstLoadReveal,
  subscribeMotionTier,
} from 'shared/effects';
import type { CelebrationKind, MotionTier } from 'shared/effects';

export type EffectsTrigger = CelebrationKind | 'reveal' | 'gameOpen';

export interface EffectsState {
  /** Boot flag as read at page load; changing it needs a reload. */
  fxEnabled: boolean;
  tier: MotionTier;
  /** Effects in flight right now (the layer unmounts at 0). */
  activeCount: number;
  layerMounted: boolean;
}

const DEMO_AMOUNT = '12,345.67';

export class EffectsController {
  private onChange: (() => void) | null = null;
  private unsubscribers: Array<() => void> = [];

  init(onChange: () => void): void {
    this.onChange = onChange;
    this.unsubscribers.push(
      effectsBus.subscribe(() => this.onChange?.()),
      subscribeMotionTier(() => this.onChange?.()),
    );
  }

  destroy(): void {
    this.unsubscribers.forEach((unsubscribe) => unsubscribe());
    this.unsubscribers = [];
    this.onChange = null;
  }

  getState(): EffectsState {
    return {
      fxEnabled: FX_ENABLED,
      tier: getMotionTier(),
      activeCount: effectsBus.getActive().length,
      layerMounted: effectsBus.isLayerMounted(),
    };
  }

  play(trigger: EffectsTrigger): void {
    if (!FX_ENABLED) return;

    switch (trigger) {
      case 'reveal':
        effectsBus.play('reveal', {});
        return;
      case 'gameOpen': {
        // A card-sized rect in the middle of the viewport.
        const width = Math.min(160, window.innerWidth * 0.35);
        const height = width * 1.378;
        effectsBus.play('gameOpen', {
          rect: { left: (window.innerWidth - width) / 2, top: (window.innerHeight - height) / 2, width, height },
        });
        return;
      }
      case 'jackpotHit':
        effectsBus.playCelebration('jackpotHit', { amount: DEMO_AMOUNT, label: 'fx_jackpot_hit' });
        return;
      case 'bigWin':
        effectsBus.playCelebration('bigWin', { amount: DEMO_AMOUNT, label: 'fx_big_win' });
        return;
      case 'confetti':
        effectsBus.playCelebration('confetti');
        return;
    }
  }

  setTier(tier: MotionTier): void {
    if (!FX_ENABLED) return;
    applyMotionTier(tier);
  }

  /** Persists the flag for the next boot. Returns false when storage is unavailable. */
  setEnabled(enabled: boolean): boolean {
    return persistFxEnabled(enabled);
  }

  resetReveal(): void {
    resetFirstLoadReveal();
  }
}
